/**
 * The one place Q274's "only what the fan does not have, and only what this
 * browser can use" rule lives. The onboarding sign-in-methods step renders
 * exactly the offers this module resolves and nothing else.
 */
import {
  isMethodLinked,
  type typeSignInMethodKey,
  type typeSignInMethodsData,
} from "../signInMethods/signInMethodsScreenLogic";

export type typeSignInMethodOffer = typeSignInMethodKey;

/** Frame order: Apple, Google, then email. */
export const SIGN_IN_METHOD_OFFER_ORDER = ["apple", "google", "email"] as const;

export type typeProviderIds = {
  appleWebServicesId: string | null;
  googleWebClientId: string | null;
};

/**
 * A usable id is a non-empty string. Null, undefined, and "" all mean the
 * operator has not configured this provider, so there is nothing to hand the
 * provider's JavaScript and nothing to draw.
 */
function isUsableId(candidate: string | null | undefined): candidate is string {
  return typeof candidate === "string" && candidate !== "";
}

export function resolveProviderIds(
  config:
    | {
        apple?:
          { isEnabled: boolean; webServicesId?: string | null } | undefined;
        google?:
          { isEnabled: boolean; webClientId?: string | null } | undefined;
      }
    | null
    | undefined,
): typeProviderIds {
  const apple = config?.apple;
  const google = config?.google;
  return {
    appleWebServicesId:
      apple?.isEnabled === true && isUsableId(apple.webServicesId)
        ? apple.webServicesId
        : null,
    googleWebClientId:
      google?.isEnabled === true && isUsableId(google.webClientId)
        ? google.webClientId
        : null,
  };
}

export function resolveSignInMethodOffers(input: {
  methods: typeSignInMethodsData | null;
  providerIds: typeProviderIds;
}): readonly typeSignInMethodOffer[] {
  const { methods, providerIds } = input;
  // A read this app could not complete offers nothing, because a
  // zero-information screen is worse than no screen, and Q274 forbids
  // rendering a screen with no rows.
  if (methods === null) return [];

  return SIGN_IN_METHOD_OFFER_ORDER.filter((method) => {
    if (isMethodLinked(methods, method)) return false;
    if (method === "apple") return providerIds.appleWebServicesId !== null;
    if (method === "google") return providerIds.googleWebClientId !== null;
    return true;
  });
}
